import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, LogIn, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { authFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const chatFailure = (error, text) => {
  const status = error?.status;
  if (status === 401) return { text, signedOut: true, message: 'You were signed out, so Duti AI couldn\'t answer.' };
  if (!status) return { text, signedOut: false, message: 'Connection to Duti AI dropped before the answer arrived.' };
  return { text, signedOut: false, message: error.message || 'Duti AI couldn\'t answer just now.' };
};

export const chatHttpError = async (res) => {
  const body = await res.json().catch(() => ({}));
  const err = new Error(body.detail || `HTTP ${res.status}`);
  err.status = res.status;
  return err;
};

export const ChatRetryNotice = ({ failure, onRetry, testId = 'chat-retry', signInPath = '/login' }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [offline, setOffline] = useState(false);
  const [checking, setChecking] = useState(false);
  const [signedOut, setSignedOut] = useState(failure.signedOut);

  const reconnect = async () => {
    setChecking(true);
    setOffline(false);
    try {
      const res = await authFetch(`${API_URL}/api/auth/me`);
      if (res.status === 401) { setSignedOut(true); return; }
      onRetry(failure.text);
    } catch {
      setOffline(true);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" role="alert" data-testid={`${testId}-notice`}>
      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <p data-testid={`${testId}-message`}>
          {signedOut ? 'You were signed out. Sign in again, then ask your question once more.'
            : offline ? 'Still can\'t reach Duti AI. Check the internet connection, then try again.' : failure.message}
        </p>
        {signedOut ? (
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={async () => { const p = await logout().catch(() => null); navigate(p === '/try-demo' ? p : signInPath); }} data-testid={`${testId}-signin-btn`}>
            <LogIn className="h-3 w-3" /> Sign in again
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={reconnect} disabled={checking} data-testid={`${testId}-btn`}>
            {checking ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Reconnect and retry
          </Button>
        )}
      </div>
    </div>
  );
};
